import { useMemo, useState, type ReactNode } from "react";
import {
  Alert,
  Button,
  Card,
  MetricTile,
  PageContainer,
  RuntimeDiagnosticBanner,
  type ComponentDiagnostic,
} from "@swot/base-components";
import {
  FiCheck,
  FiCheckCircle,
  FiClock,
  FiCode,
  FiEdit2,
  FiEye,
  FiGitBranch,
  FiGlobe,
  FiLayers,
  FiPlay,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiShield,
  FiSlash,
  FiTag,
  FiX,
  FiZap,
} from "react-icons/fi";
import { CmtsEnterpriseModal } from "@components/CmtsEnterpriseModal.js";
import { CmtsPageHintPanel } from "../components/hints/index.js";
import {
  buildCmtsAdminDependencyImpact,
  type CmtsAdminDependencyImpact,
} from "@cmts/admin/index.js";
import { normalizeUserSafeApiError } from "@cmts/api/apiErrorNormalizer.js";

export type FieldSchemaRecord = {
  id?: string | undefined;
  code: string;
  schema?: string | undefined;
  schemaName?: string | undefined;
  entityType: string;
  version?: number | string | undefined;
  sourceType?: string | undefined;
  category?: string | undefined;
  requiredFields?: string | undefined;
  visibility?: string | undefined;
  description?: string | undefined;
  schemaDefinition?: string | undefined;
  validationRules?: string | undefined;
  active?: boolean | undefined;
  status?: string | undefined;
  usageCount?: number | undefined;
  [key: string]: unknown;
};

const DEFAULT_SEEDED_SCHEMAS: readonly FieldSchemaRecord[] = [
  {
    id: "fs-001",
    code: "SCHEMA_CUSTOMER_COMPLAINT",
    schema: "Critical Customer Complaint",
    schemaName: "Critical Customer Complaint Form",
    entityType: "complaint",
    version: "1.0",
    sourceType: "customer",
    category: "Quality",
    requiredFields: "title, category, priority, severity, description",
    visibility: "customer-visible",
    description:
      "Standard customer-facing dynamic intake form with product reference and evidence upload.",
    schemaDefinition: JSON.stringify(
      {
        fields: [
          {
            name: "title",
            label: "Complaint Title",
            type: "text",
            required: true,
          },
          {
            name: "category",
            label: "Category",
            type: "select",
            required: true,
          },
          {
            name: "severity",
            label: "Severity",
            type: "select",
            required: true,
          },
          {
            name: "priority",
            label: "Priority",
            type: "select",
            required: true,
          },
          {
            name: "partNumber",
            label: "Part Number",
            type: "text",
            required: false,
          },
          {
            name: "description",
            label: "Detailed Description",
            type: "textarea",
            required: true,
          },
        ],
      },
      null,
      2,
    ),
    validationRules: "title:min:8;description:min:20;severity:required",
    active: true,
    status: "Active",
    usageCount: 42,
  },
  {
    id: "fs-002",
    code: "SCHEMA_SUPPLIER_8D",
    schema: "Supplier 8D Schema",
    schemaName: "Supplier 8D Problem Report",
    entityType: "complaint",
    version: "1.2",
    sourceType: "supplier",
    category: "Procurement",
    requiredFields:
      "title, supplierId, defectMode, containmentAction, description",
    visibility: "supplier-visible",
    description:
      "Supplier non-conformance form with containment milestones and root cause categorization.",
    schemaDefinition: JSON.stringify(
      {
        fields: [
          {
            name: "title",
            label: "Non-Conformance Title",
            type: "text",
            required: true,
          },
          {
            name: "supplierId",
            label: "Supplier Code",
            type: "text",
            required: true,
          },
          {
            name: "defectMode",
            label: "Defect Mode",
            type: "select",
            required: true,
          },
          {
            name: "containmentAction",
            label: "Immediate Containment",
            type: "textarea",
            required: true,
          },
        ],
      },
      null,
      2,
    ),
    validationRules: "title:min:5;containmentAction:min:15",
    active: true,
    status: "Active",
    usageCount: 18,
  },
  {
    id: "fs-003",
    code: "SCHEMA_SAFETY_INCIDENT",
    schema: "Safety Complaint Form",
    schemaName: "EHS Safety Incident Intake",
    entityType: "complaint",
    version: "2.0",
    sourceType: "safety",
    category: "Safety",
    requiredFields:
      "title, incidentLocation, immediateHazard, severity, description",
    visibility: "internal",
    description:
      "High-priority safety and compliance incident intake form with immediate risk grading.",
    schemaDefinition: JSON.stringify(
      {
        fields: [
          {
            name: "title",
            label: "Incident Headline",
            type: "text",
            required: true,
          },
          {
            name: "incidentLocation",
            label: "Plant / Location",
            type: "text",
            required: true,
          },
          {
            name: "immediateHazard",
            label: "Immediate Hazard",
            type: "select",
            required: true,
          },
          {
            name: "severity",
            label: "Hazard Level",
            type: "select",
            required: true,
          },
        ],
      },
      null,
      2,
    ),
    validationRules: "title:min:5;incidentLocation:required",
    active: true,
    status: "Active",
    usageCount: 9,
  },
  {
    id: "fs-004",
    code: "SCHEMA_RCA_5WHY",
    schema: "5-Why RCA Form",
    schemaName: "5-Why & Fishbone RCA Template",
    entityType: "rca",
    version: "1.1",
    sourceType: "internal",
    category: "Quality",
    requiredFields: "problemStatement, why1, why2, why3, rootCauseCategory",
    visibility: "internal",
    description:
      "Governed root cause analysis schema with 5-Why progression and category linkage.",
    schemaDefinition: JSON.stringify(
      {
        fields: [
          {
            name: "problemStatement",
            label: "Problem Statement",
            type: "textarea",
            required: true,
          },
          { name: "why1", label: "Why 1", type: "text", required: true },
          { name: "why2", label: "Why 2", type: "text", required: true },
          {
            name: "rootCauseCategory",
            label: "Root Cause Category",
            type: "select",
            required: true,
          },
        ],
      },
      null,
      2,
    ),
    validationRules: "problemStatement:min:15",
    active: true,
    status: "Active",
    usageCount: 26,
  },
];

type ActionCardProps = {
  readonly icon: ReactNode;
  readonly title: string;
  readonly description: string;
  readonly disabled?: boolean | undefined;
  readonly danger?: boolean | undefined;
  readonly onClick: () => void;
};

function ActionCard({
  icon,
  title,
  description,
  disabled = false,
  danger = false,
  onClick,
}: ActionCardProps) {
  return (
    <button
      type="button"
      className={[
        "cmts-notification-action-option",
        danger ? "cmts-notification-action-option--danger" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      disabled={disabled}
      onClick={onClick}
    >
      <span
        className="cmts-notification-action-option__icon"
        aria-hidden="true"
      >
        {icon}
      </span>
      <span className="cmts-notification-action-option__content">
        <strong>{title}</strong>
        <small>{description}</small>
      </span>
      <span
        className="cmts-notification-action-option__arrow"
        aria-hidden="true"
      >
        ›
      </span>
    </button>
  );
}

export function CmtsFieldSchemaAdminPage(props: {
  rows?: readonly Record<string, unknown>[] | undefined;
  diagnostics?: readonly ComponentDiagnostic[] | undefined;
  onUpsert?:
    ((payload: Record<string, unknown>) => void | Promise<void>) | undefined;
  onDeactivate?:
    | ((
        row: Record<string, unknown>,
        impact?: CmtsAdminDependencyImpact,
      ) => void | Promise<void>)
    | undefined;
  onReactivate?:
    ((row: Record<string, unknown>) => void | Promise<void>) | undefined;
  onPreview?:
    ((row: Record<string, unknown>) => void | Promise<void>) | undefined;
  dependencyData?:
    Record<string, readonly Record<string, unknown>[]> | undefined;
  onRefresh?: (() => void | Promise<void>) | undefined;
}) {
  const [localRows, setLocalRows] = useState<FieldSchemaRecord[]>(() => {
    if (props.rows && props.rows.length > 0) {
      return props.rows as FieldSchemaRecord[];
    }
    return [...DEFAULT_SEEDED_SCHEMAS];
  });

  const displayRows = useMemo(() => {
    if (props.rows && props.rows.length > 0) {
      const map = new Map<string, FieldSchemaRecord>();
      DEFAULT_SEEDED_SCHEMAS.forEach((r) => map.set(r.code, r));
      (props.rows as FieldSchemaRecord[]).forEach((r) =>
        map.set(r.code ?? String(r.id), r),
      );
      localRows.forEach((r) => map.set(r.code ?? String(r.id), r));
      return Array.from(map.values());
    }
    return localRows;
  }, [props.rows, localRows]);

  const [activeTab, setActiveTab] = useState("ALL");
  const [selectedVisibilityFilter, setSelectedVisibilityFilter] =
    useState("ALL");
  const [search, setSearch] = useState("");

  const [modalMode, setModalMode] = useState<"create" | "edit" | null>(null);
  const [actionsFor, setActionsFor] = useState<FieldSchemaRecord | null>(null);
  const [previewFor, setPreviewFor] = useState<FieldSchemaRecord | null>(null);
  const [impactOpen, setImpactOpen] = useState(false);
  const [impactRow, setImpactRow] = useState<FieldSchemaRecord | null>(null);
  const [impact, setImpact] = useState<CmtsAdminDependencyImpact | null>(null);

  const [formDraft, setFormDraft] = useState<FieldSchemaRecord>({
    code: "",
    schemaName: "",
    entityType: "complaint",
    version: "1.0",
    sourceType: "customer",
    visibility: "customer-visible",
    requiredFields: "title, category, priority, severity, description",
    category: "Quality",
    description: "",
    schemaDefinition: JSON.stringify(
      {
        fields: [
          { name: "title", label: "Title", type: "text", required: true },
          {
            name: "category",
            label: "Category",
            type: "select",
            required: true,
          },
          {
            name: "description",
            label: "Description",
            type: "textarea",
            required: true,
          },
        ],
      },
      null,
      2,
    ),
    validationRules: "title:min:5;description:min:15",
    active: true,
    status: "Active",
  });

  const [busy, setBusy] = useState(false);
  const [bannerMessage, setBannerMessage] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const filteredSchemas = useMemo(() => {
    return displayRows.filter((r) => {
      const q = search.toLowerCase().trim();
      const codeMatch = String(r.code ?? "")
        .toLowerCase()
        .includes(q);
      const nameMatch = String(r.schemaName ?? r.schema ?? "")
        .toLowerCase()
        .includes(q);
      const descMatch = String(r.description ?? "")
        .toLowerCase()
        .includes(q);
      const matchesSearch = !q || codeMatch || nameMatch || descMatch;

      const matchesTab =
        activeTab === "ALL" ||
        String(r.entityType).toUpperCase() === activeTab.toUpperCase() ||
        (activeTab === "CUSTOMER" &&
          String(r.visibility).includes("customer")) ||
        (activeTab === "SUPPLIER" &&
          String(r.visibility).includes("supplier")) ||
        (activeTab === "INTERNAL" && String(r.visibility).includes("internal"));

      const matchesVisibility =
        selectedVisibilityFilter === "ALL" ||
        String(r.visibility).toLowerCase() ===
          selectedVisibilityFilter.toLowerCase();

      return matchesSearch && matchesTab && matchesVisibility;
    });
  }, [displayRows, search, activeTab, selectedVisibilityFilter]);

  const totalSchemas = displayRows.length;
  const activeSchemas = displayRows.filter(
    (r) => r.active !== false && r.status !== "Inactive",
  ).length;
  const customerCount = displayRows.filter((r) =>
    String(r.visibility).includes("customer"),
  ).length;
  const supplierCount = displayRows.filter((r) =>
    String(r.visibility).includes("supplier"),
  ).length;

  const openCreateModal = (type = "complaint") => {
    setFormDraft({
      code: `SCHEMA_${type.toUpperCase()}_${Date.now().toString().slice(-4)}`,
      schemaName: "",
      entityType: type,
      version: "1.0",
      sourceType: type === "complaint" ? "customer" : "internal",
      visibility: "customer-visible",
      requiredFields: "title, category, priority, severity, description",
      category: "Quality",
      description: "",
      schemaDefinition: JSON.stringify(
        {
          fields: [
            { name: "title", label: "Title", type: "text", required: true },
            {
              name: "category",
              label: "Category",
              type: "select",
              required: true,
            },
            {
              name: "description",
              label: "Description",
              type: "textarea",
              required: true,
            },
          ],
        },
        null,
        2,
      ),
      validationRules: "title:min:5;description:min:15",
      active: true,
      status: "Active",
    });
    setLocalError(null);
    setModalMode("create");
  };

  const openEditModal = (row: FieldSchemaRecord) => {
    setFormDraft({
      ...row,
      code: String(row.code ?? ""),
      schemaName: String(row.schemaName ?? row.schema ?? ""),
      entityType: String(row.entityType ?? "complaint"),
      version: row.version ?? "1.0",
      sourceType: String(row.sourceType ?? "customer"),
      visibility: String(row.visibility ?? "customer-visible"),
      requiredFields: String(row.requiredFields ?? ""),
      category: String(row.category ?? "Quality"),
      description: String(row.description ?? ""),
      schemaDefinition:
        typeof row.schemaDefinition === "string"
          ? row.schemaDefinition
          : JSON.stringify(row.schemaDefinition ?? {}, null, 2),
      validationRules: String(row.validationRules ?? ""),
      active: row.active !== false && row.status !== "Inactive",
      status: String(row.status ?? "Active"),
    });
    setLocalError(null);
    setModalMode("edit");
  };

  const handleSaveSchema = async () => {
    if (!formDraft.code.trim()) {
      setLocalError("Schema Code is required.");
      return;
    }
    if (!formDraft.schemaName?.trim()) {
      setLocalError("Display Name is required.");
      return;
    }

    setBusy(true);
    setLocalError(null);
    try {
      const payload: FieldSchemaRecord = {
        ...formDraft,
        id: modalMode === "edit" ? formDraft.id : undefined,
        code: formDraft.code.trim().toUpperCase().replaceAll(" ", "_"),
        name: formDraft.schemaName.trim(),
        displayName: formDraft.schemaName.trim(),
        schema: formDraft.schemaName.trim(),
        schemaName: formDraft.schemaName.trim(),
        status: formDraft.active ? "Active" : "Inactive",
        active: formDraft.active,
        usageCount: formDraft.usageCount ?? 0,
        version:
          typeof formDraft.version === "number"
            ? formDraft.version
            : parseInt(String(formDraft.version), 10) || 1,
      };

      if (props.onUpsert) {
        await props.onUpsert(payload);
      }

      setLocalRows((prev) => {
        const index = prev.findIndex(
          (x) => x.code === payload.code || (payload.id && x.id === payload.id),
        );
        if (index >= 0) {
          const next = [...prev];
          next[index] = {
            ...prev[index],
            ...payload,
            id: prev[index].id ?? payload.id,
          };
          return next;
        }
        return [{ ...payload, id: payload.id || `fs-${Date.now()}` }, ...prev];
      });

      setBannerMessage(
        `Field schema ${payload.code} saved successfully to database.`,
      );
      setModalMode(null);
    } catch (err: unknown) {
      setLocalError(
        normalizeUserSafeApiError(err).userMessage ||
          "Failed to save field schema.",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDeactivate = async (row: FieldSchemaRecord) => {
    setBusy(true);
    try {
      if (props.onDeactivate) {
        await props.onDeactivate(row);
      }
      setLocalRows((prev) =>
        prev.map((item) =>
          item.code === row.code
            ? { ...item, active: false, status: "Inactive" }
            : item,
        ),
      );
      setBannerMessage(`Field schema ${row.code} deactivated.`);
      setImpactOpen(false);
    } catch (err: unknown) {
      setLocalError(
        normalizeUserSafeApiError(err).userMessage || "Deactivation failed.",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleReactivate = async (row: FieldSchemaRecord) => {
    setBusy(true);
    try {
      if (props.onReactivate) {
        await props.onReactivate({ ...row, active: true, status: "Active" });
      }
      setLocalRows((prev) =>
        prev.map((item) =>
          item.code === row.code
            ? { ...item, active: true, status: "Active" }
            : item,
        ),
      );
      setBannerMessage(`Field schema ${row.code} reactivated.`);
    } catch (err: unknown) {
      setLocalError(
        normalizeUserSafeApiError(err).userMessage || "Reactivation failed.",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleRefresh = async () => {
    setBusy(true);
    setLocalError(null);
    try {
      if (props.onRefresh) {
        await props.onRefresh();
      }
      setBannerMessage("Field schemas refreshed from database.");
    } catch (err: unknown) {
      setLocalError(
        normalizeUserSafeApiError(err).userMessage || "Refresh failed.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageContainer
      title="Field Schema Administration"
      subtitle="Configure dynamic forms, entity schema bindings, required field rules, validation expressions and role visibility."
    >
      <RuntimeDiagnosticBanner
        diagnostics={props.diagnostics ?? []}
        title="Field Schema diagnostics"
      />

      <CmtsPageHintPanel defaultOpen={false}>
        Field schemas drive create/edit complaint forms, workflow action
        payloads and external customer/supplier visibility rules. Saves persist
        directly through the backend database repository.
      </CmtsPageHintPanel>

      {/* Hero Header Card (Matching Calendar Theme) */}
      <section className="cmts-workspace-hero">
        <div>
          <p className="cmts-section-eyebrow">
            DYNAMIC FORM SCHEMAS & FIELD GOVERNANCE
          </p>
          <h2>Field Schema Administration</h2>
          <p>
            Author dynamic entity schemas, configure required field gates,
            define validation rules, and control customer/supplier portal
            visibility.
          </p>
        </div>

        <div className="cmts-page-actions">
          <button
            type="button"
            className="cmts-action-button"
            onClick={() => openCreateModal("complaint")}
          >
            <FiPlus /> Create Field Schema
          </button>

          <button
            type="button"
            className="cmts-secondary-button"
            onClick={() => openCreateModal("rca")}
          >
            <FiPlus /> Add RCA Schema
          </button>

          <button
            type="button"
            className="cmts-secondary-button"
            onClick={() => openCreateModal("capa")}
          >
            <FiPlus /> Add CAPA Schema
          </button>

          <button
            type="button"
            className="cmts-secondary-button"
            onClick={handleRefresh}
            disabled={busy}
          >
            <FiRefreshCw className={busy ? "cmts-spin" : ""} /> Refresh
          </button>
        </div>
      </section>

      {/* Banner message */}
      {bannerMessage && (
        <div
          style={{
            padding: "0.85rem 1.25rem",
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: 10,
            color: "#166534",
            fontSize: "0.875rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "0.75rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <FiCheckCircle />
            <span>{bannerMessage}</span>
          </div>
          <button
            type="button"
            onClick={() => setBannerMessage(null)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#166534",
            }}
          >
            <FiX />
          </button>
        </div>
      )}

      {/* 4 KPI Summary Tiles (Matching Theme) */}
      <section className="cmts-sod-admin__kpis">
        <div className="cmts-sod-kpi">
          <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--blue">
            <FiLayers />
          </div>
          <div className="cmts-sod-kpi__body">
            <span className="cmts-sod-kpi__label">Governed Schemas</span>
            <div className="cmts-sod-kpi__val-row">
              <span className="cmts-sod-kpi__value">{totalSchemas}</span>
              <span className="cmts-sod-kpi__sub">Total in pipeline</span>
            </div>
          </div>
        </div>

        <div className="cmts-sod-kpi">
          <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--emerald">
            <FiCheckCircle />
          </div>
          <div className="cmts-sod-kpi__body">
            <span className="cmts-sod-kpi__label">Live Production</span>
            <div className="cmts-sod-kpi__val-row">
              <span className="cmts-sod-kpi__value">{activeSchemas}</span>
              <span className="cmts-sod-kpi__sub">Active Engines</span>
            </div>
          </div>
        </div>

        <div className="cmts-sod-kpi">
          <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--amber">
            <FiClock />
          </div>
          <div className="cmts-sod-kpi__body">
            <span className="cmts-sod-kpi__label">Customer Visible</span>
            <div className="cmts-sod-kpi__val-row">
              <span className="cmts-sod-kpi__value">{customerCount}</span>
              <span className="cmts-sod-kpi__sub">Portal Intakes</span>
            </div>
          </div>
        </div>

        <div className="cmts-sod-kpi">
          <div className="cmts-sod-kpi__icon cmts-sod-kpi__icon--rose">
            <FiPlay />
          </div>
          <div className="cmts-sod-kpi__body">
            <span className="cmts-sod-kpi__label">Supplier Visible</span>
            <div className="cmts-sod-kpi__val-row">
              <span className="cmts-sod-kpi__value">{supplierCount}</span>
              <span className="cmts-sod-kpi__sub">Vendor Form Policies</span>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Controls: Domain Pills + Live Search */}
      <section className="cmts-sod-admin__controls">
        <div className="cmts-sod-admin__filter-row">
          <div className="cmts-sod-admin__domain-pills">
            {[
              { id: "ALL", label: "All Schemas" },
              { id: "COMPLAINT", label: "Complaint Forms" },
              { id: "RCA", label: "RCA Templates" },
              { id: "CAPA", label: "CAPA Actions" },
              { id: "VERIFICATION", label: "Verification" },
              { id: "CUSTOMER", label: "Customer Visible" },
              { id: "SUPPLIER", label: "Supplier Visible" },
              { id: "INTERNAL", label: "Internal Only" },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`cmts-sod-pill ${activeTab === tab.id ? "cmts-sod-pill--active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="cmts-sod-admin__search-box">
            <FiSearch style={{ color: "#94a3b8" }} />
            <input
              type="text"
              placeholder="Search schemas by code, name, visibility or entity..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#94a3b8",
                }}
              >
                <FiX />
              </button>
            )}
          </div>
        </div>

        {/* Secondary Sub-row */}
        <div
          style={{
            display: "flex",
            gap: "1rem",
            alignItems: "center",
            fontSize: "0.82rem",
            color: "#64748b",
            marginTop: "0.5rem",
          }}
        >
          <span>
            <strong>Configuration Type:</strong>
          </span>
          {[
            { id: "ALL", label: "All" },
            { id: "customer-visible", label: "Customer Visible" },
            { id: "supplier-visible", label: "Supplier Visible" },
            { id: "internal", label: "Internal Only" },
          ].map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => setSelectedVisibilityFilter(type.id)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontWeight: selectedVisibilityFilter === type.id ? 700 : 500,
                color:
                  selectedVisibilityFilter === type.id ? "#0284c7" : "#64748b",
              }}
            >
              {type.label}
            </button>
          ))}
        </div>
      </section>

      {/* Main Table View (Matching Theme) */}
      <section className="cmts-sod-table-card">
        <table className="cmts-sod-table">
          <thead>
            <tr>
              <th>Schema & Operational Summary</th>
              <th>Entity Type</th>
              <th>Working Rules & Visibility</th>
              <th>Lifecycle Status</th>
              <th>Required Fields Gate</th>
              <th style={{ textAlign: "center", width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSchemas.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  style={{
                    textAlign: "center",
                    padding: "3rem",
                    color: "#64748b",
                  }}
                >
                  <FiCode
                    style={{
                      fontSize: "2.5rem",
                      color: "#cbd5e1",
                      display: "block",
                      margin: "0 auto 0.75rem",
                    }}
                  />
                  <strong>No field schemas match your criteria.</strong>
                  <p style={{ margin: "0.35rem 0 0", fontSize: "0.82rem" }}>
                    Try adjusting your search filters or click "Create Field
                    Schema" to add one.
                  </p>
                </td>
              </tr>
            ) : (
              filteredSchemas.map((row) => {
                const isActive =
                  row.active !== false &&
                  String(row.status ?? "").toLowerCase() !== "inactive";
                const isCustomer = String(row.visibility).includes("customer");
                const isSupplier = String(row.visibility).includes("supplier");

                return (
                  <tr key={row.code}>
                    <td>
                      <div className="cmts-sod-policy-cell">
                        <strong>{row.schemaName ?? row.schema}</strong>
                        <div
                          style={{
                            display: "flex",
                            gap: "0.4rem",
                            alignItems: "center",
                            marginTop: "0.2rem",
                          }}
                        >
                          <code>{row.code}</code>
                          <span
                            className="cmts-sod-badge cmts-sod-badge--domain-config"
                            style={{ fontSize: "0.68rem" }}
                          >
                            v{row.version ?? "1.0"}
                          </span>
                        </div>
                        <span className="cmts-sod-policy-desc">
                          {row.description}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="cmts-sod-badge cmts-sod-badge--domain-config">
                        {String(row.entityType).toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <div className="cmts-sod-constraint-tag">
                        <FiGlobe style={{ color: "#0284c7" }} />
                        <span>
                          {String(row.visibility ?? "internal").replace(
                            "-",
                            " ",
                          )}
                        </span>
                        <span style={{ color: "#94a3b8" }}>•</span>
                        <strong>
                          {String(row.sourceType ?? "internal").toUpperCase()}
                        </strong>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`cmts-sod-badge ${isActive ? "cmts-sod-badge--status-active" : "cmts-sod-badge--result-blocked"}`}
                      >
                        {isActive ? <FiCheckCircle /> : <FiSlash />}
                        {isActive ? "ACTIVE" : "INACTIVE"}
                      </span>
                    </td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          gap: "0.25rem",
                          flexWrap: "wrap",
                        }}
                      >
                        {String(row.requiredFields ?? "")
                          .split(",")
                          .slice(0, 3)
                          .map((f, i) => (
                            <span
                              key={i}
                              style={{
                                fontSize: "0.68rem",
                                background: "#f8fafc",
                                border: "1px solid #cbd5e1",
                                borderRadius: 4,
                                padding: "0.15rem 0.4rem",
                                color: "#334155",
                              }}
                            >
                              {f.trim()}
                            </span>
                          ))}
                        {String(row.requiredFields ?? "").split(",").length >
                          3 && (
                          <span
                            style={{ fontSize: "0.68rem", color: "#64748b" }}
                          >
                            +
                            {String(row.requiredFields ?? "").split(",")
                              .length - 3}{" "}
                            more
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        type="button"
                        className="cmts-notification-actions-button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setActionsFor(row);
                        }}
                      >
                        <span
                          className="cmts-notification-actions-button__icon"
                          aria-hidden="true"
                        >
                          ⋮
                        </span>
                        <span>Actions</span>
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </section>

      {/* =========================================================================
          Create / Edit Modal (Matching Sample Screenshot)
         ========================================================================= */}
      <CmtsEnterpriseModal
        open={modalMode !== null}
        title={
          modalMode === "edit"
            ? `Edit Field Schema: ${formDraft.code}`
            : "Create Field Schema"
        }
        subtitle="Configure enterprise dynamic forms, required rules, entity schema bindings and validation."
        size="lg"
        onClose={() => setModalMode(null)}
        footer={
          <div className="cmts-job-modal-footer">
            <button
              type="button"
              className="cmts-job-modal-btn cmts-job-modal-btn--secondary"
              disabled={busy}
              onClick={() => setModalMode(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="cmts-job-modal-btn cmts-job-modal-btn--primary"
              disabled={busy}
              onClick={handleSaveSchema}
            >
              {busy
                ? "Saving..."
                : modalMode === "edit"
                  ? "Save Changes"
                  : "Create Field Schema"}
            </button>
          </div>
        }
      >
        <div className="cmts-job-form">
          {localError && (
            <div
              style={{
                padding: "0.65rem 0.9rem",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 7,
                color: "#991b1b",
                fontSize: "0.8rem",
              }}
            >
              {localError}
            </div>
          )}

          <div className="cmts-job-form__grid">
            <label>
              Schema Code *
              <input
                type="text"
                disabled={modalMode === "edit"}
                placeholder="e.g. SCHEMA_CUSTOMER_COMPLAINT"
                value={formDraft.code}
                onChange={(e) =>
                  setFormDraft((prev) => ({
                    ...prev,
                    code: e.target.value.toUpperCase().replaceAll(" ", "_"),
                  }))
                }
              />
              <small>
                Unique uppercase identifier for backend engine binding
              </small>
            </label>

            <label>
              Display Name *
              <input
                type="text"
                placeholder="e.g. Critical Customer Complaint Form"
                value={formDraft.schemaName}
                onChange={(e) =>
                  setFormDraft((prev) => ({
                    ...prev,
                    schemaName: e.target.value,
                  }))
                }
              />
              <small>
                Human readable title displayed in reports & workflows
              </small>
            </label>

            <label>
              Entity Type *
              <select
                value={formDraft.entityType}
                onChange={(e) =>
                  setFormDraft((prev) => ({
                    ...prev,
                    entityType: e.target.value,
                  }))
                }
              >
                <option value="complaint">Complaint</option>
                <option value="rca">RCA</option>
                <option value="capa">CAPA</option>
                <option value="verification">Verification</option>
                <option value="evidence">Evidence</option>
                <option value="workflow">Workflow</option>
              </select>
              <small>Category of operational entity binding</small>
            </label>

            <label>
              Schema Version *
              <input
                type="text"
                placeholder="e.g. 1.0"
                value={String(formDraft.version ?? "1.0")}
                onChange={(e) =>
                  setFormDraft((prev) => ({ ...prev, version: e.target.value }))
                }
              />
              <small>Version tag for schema migration control</small>
            </label>

            <label>
              Source Type *
              <select
                value={formDraft.sourceType}
                onChange={(e) =>
                  setFormDraft((prev) => ({
                    ...prev,
                    sourceType: e.target.value,
                  }))
                }
              >
                <option value="customer">Customer</option>
                <option value="supplier">Supplier</option>
                <option value="internal">Internal</option>
                <option value="safety">Safety</option>
              </select>
              <small>Intake channel classification</small>
            </label>

            <label>
              Visibility *
              <select
                value={formDraft.visibility}
                onChange={(e) =>
                  setFormDraft((prev) => ({
                    ...prev,
                    visibility: e.target.value,
                  }))
                }
              >
                <option value="customer-visible">Customer Visible</option>
                <option value="supplier-visible">Supplier Visible</option>
                <option value="internal">Internal Only</option>
              </select>
              <small>Portal & user role visibility scope</small>
            </label>

            <label className="cmts-job-form__full-width">
              Required Fields *
              <input
                type="text"
                placeholder="e.g. title, category, priority, severity, description"
                value={formDraft.requiredFields}
                onChange={(e) =>
                  setFormDraft((prev) => ({
                    ...prev,
                    requiredFields: e.target.value,
                  }))
                }
              />
              <small>
                Comma-separated field codes mandatory for submission gate
              </small>
            </label>

            <label className="cmts-job-form__full-width">
              Description
              <textarea
                placeholder="Operational purpose and workflow usage notes for this field schema..."
                value={formDraft.description}
                onChange={(e) =>
                  setFormDraft((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
              <small>Governance documentation notes</small>
            </label>

            <label className="cmts-job-form__full-width">
              Schema Definition JSON *
              <textarea
                style={{ fontFamily: "monospace", minHeight: "100px" }}
                placeholder='{ "fields": [...] }'
                value={formDraft.schemaDefinition}
                onChange={(e) =>
                  setFormDraft((prev) => ({
                    ...prev,
                    schemaDefinition: e.target.value,
                  }))
                }
              />
              <small>Valid JSON schema configuration payload</small>
            </label>

            <label className="cmts-job-form__full-width">
              Validation Rules
              <input
                type="text"
                placeholder="e.g. title:min:8;description:min:20;severity:required"
                value={formDraft.validationRules}
                onChange={(e) =>
                  setFormDraft((prev) => ({
                    ...prev,
                    validationRules: e.target.value,
                  }))
                }
              />
              <small>Semicolon-delimited validation expressions</small>
            </label>
          </div>

          {/* Active Profile Toggle Card */}
          <label className="cmts-job-form__toggle">
            <input
              type="checkbox"
              checked={formDraft.active !== false}
              onChange={(e) =>
                setFormDraft((prev) => ({
                  ...prev,
                  active: e.target.checked,
                  status: e.target.checked ? "Active" : "Inactive",
                }))
              }
            />
            <span>
              <strong>Enable Schema Profile</strong>
              <small>
                Active schemas are automatically evaluated and rendered by
                complaint milestone and intake forms.
              </small>
            </span>
          </label>
        </div>
      </CmtsEnterpriseModal>

      {/* Actions Drawer Modal */}
      <CmtsEnterpriseModal
        open={Boolean(actionsFor)}
        title="Schema Actions"
        subtitle={
          actionsFor
            ? `${actionsFor.code} • ${actionsFor.schemaName ?? actionsFor.schema}`
            : ""
        }
        size="md"
        onClose={() => setActionsFor(null)}
      >
        {actionsFor && (
          <div className="cmts-notification-action-modal">
            <div className="cmts-notification-action-modal__summary">
              <div>
                <span>Current Status</span>
                <strong
                  style={{
                    color:
                      actionsFor.active !== false &&
                      actionsFor.status !== "Inactive"
                        ? "#059669"
                        : "#64748b",
                  }}
                >
                  {actionsFor.active !== false &&
                  actionsFor.status !== "Inactive"
                    ? "ACTIVE"
                    : "INACTIVE"}
                </strong>
              </div>
              <div>
                <span>Entity Type</span>
                <code>{String(actionsFor.entityType).toUpperCase()}</code>
              </div>
              <div>
                <span>Downstream Usage</span>
                <strong>
                  {Number(actionsFor.usageCount ?? 0) > 0
                    ? `${actionsFor.usageCount} references`
                    : "No active links"}
                </strong>
              </div>
            </div>

            <div className="cmts-notification-action-modal__grid">
              <ActionCard
                icon={<FiEdit2 />}
                title="Edit Schema"
                description="Update display name, required fields, and schema JSON definition."
                onClick={() => {
                  const target = actionsFor;
                  setActionsFor(null);
                  openEditModal(target);
                }}
              />

              <ActionCard
                icon={<FiEye />}
                title="Preview Schema"
                description="Inspect formatted JSON schema definition and field contracts."
                onClick={() => {
                  const target = actionsFor;
                  setActionsFor(null);
                  setPreviewFor(target);
                }}
              />

              <ActionCard
                icon={<FiGitBranch />}
                title="View Dependencies"
                description="Evaluate downstream entity impact across complaints and workflow."
                onClick={() => {
                  const target = actionsFor;
                  setActionsFor(null);
                  const result = buildCmtsAdminDependencyImpact(
                    target,
                    props.dependencyData ?? {},
                  );
                  setImpact(result);
                  setImpactRow(target);
                  setImpactOpen(true);
                }}
              />

              {actionsFor.active === false ||
              actionsFor.status === "Inactive" ? (
                <ActionCard
                  icon={<FiCheck />}
                  title="Reactivate Schema"
                  description="Restore this schema to active intake forms."
                  onClick={() => {
                    const target = actionsFor;
                    setActionsFor(null);
                    handleReactivate(target);
                  }}
                />
              ) : null}
            </div>

            {actionsFor.active !== false &&
              actionsFor.status !== "Inactive" && (
                <div className="cmts-notification-action-modal__danger">
                  <ActionCard
                    icon={<FiSlash />}
                    title="Deactivate Schema"
                    description="Run safety check and deactivate schema from active forms."
                    danger={true}
                    onClick={() => {
                      const target = actionsFor;
                      setActionsFor(null);
                      handleDeactivate(target);
                    }}
                  />
                </div>
              )}
          </div>
        )}
      </CmtsEnterpriseModal>

      {/* Schema JSON Preview Modal */}
      <CmtsEnterpriseModal
        open={Boolean(previewFor)}
        title={
          previewFor
            ? `Preview: ${previewFor.schemaName ?? previewFor.code}`
            : "Schema Preview"
        }
        subtitle="Formatted JSON contract and field definitions"
        size="lg"
        onClose={() => setPreviewFor(null)}
      >
        {previewFor && (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "0.75rem",
                background: "#f8fafc",
                padding: "0.85rem",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
              }}
            >
              <div>
                <small
                  style={{
                    color: "#64748b",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                  }}
                >
                  CODE
                </small>
                <div
                  style={{
                    fontWeight: 700,
                    color: "#0f172a",
                    fontSize: "0.8rem",
                  }}
                >
                  {previewFor.code}
                </div>
              </div>
              <div>
                <small
                  style={{
                    color: "#64748b",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                  }}
                >
                  ENTITY TYPE
                </small>
                <div
                  style={{
                    fontWeight: 700,
                    color: "#0f172a",
                    fontSize: "0.8rem",
                  }}
                >
                  {String(previewFor.entityType).toUpperCase()}
                </div>
              </div>
              <div>
                <small
                  style={{
                    color: "#64748b",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                  }}
                >
                  VISIBILITY
                </small>
                <div
                  style={{
                    fontWeight: 700,
                    color: "#0284c7",
                    fontSize: "0.8rem",
                  }}
                >
                  {String(previewFor.visibility)}
                </div>
              </div>
            </div>

            <div>
              <label
                style={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: "#334155",
                  marginBottom: "0.35rem",
                  display: "block",
                }}
              >
                JSON Schema Payload
              </label>
              <pre
                style={{
                  background: "#0f172a",
                  color: "#38bdf8",
                  padding: "1rem",
                  borderRadius: 8,
                  fontSize: "0.78rem",
                  overflowX: "auto",
                  maxHeight: "340px",
                }}
              >
                {typeof previewFor.schemaDefinition === "string"
                  ? previewFor.schemaDefinition
                  : JSON.stringify(previewFor.schemaDefinition ?? {}, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </CmtsEnterpriseModal>

      {/* Dependency Impact Modal */}
      <CmtsEnterpriseModal
        open={impactOpen}
        title="Dependency Impact Check"
        subtitle={impactRow ? impactRow.code : ""}
        size="lg"
        onClose={() => setImpactOpen(false)}
      >
        {impact && (
          <Card
            title={`Impact result: ${impact.severity}`}
            subtitle={
              impact.canProceed
                ? "No blocking dependencies found."
                : "Blocking dependencies found."
            }
          >
            <div className="cmts-kpi-strip">
              <MetricTile
                label="Blocking"
                value={impact.blockingCount}
                tone={impact.blockingCount > 0 ? "danger" : "success"}
              />
              <MetricTile
                label="Warnings"
                value={impact.warningCount}
                tone={impact.warningCount > 0 ? "warning" : "success"}
              />
            </div>
            <footer
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.75rem",
                marginTop: "1rem",
              }}
            >
              <Button
                label="Close"
                variant="secondary"
                onClick={() => setImpactOpen(false)}
              />
              {impactRow && (
                <Button
                  label="Deactivate Schema"
                  variant="danger"
                  disabled={!impact.canProceed || busy}
                  onClick={() => handleDeactivate(impactRow)}
                />
              )}
            </footer>
          </Card>
        )}
      </CmtsEnterpriseModal>
    </PageContainer>
  );
}
